import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ContributorAuthCallback - Handles the OAuth callback from Google
 * 
 * Flow:
 * 1. Extract session_id from URL fragment
 * 2. Exchange it for contributor session via backend
 * 3. Link pending review if any
 * 4. Redirect to contributor page or return URL
 */
export default function ContributorAuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState(null);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          setError("No session ID found");
          setStatus("error");
          return;
        }
        
        const sessionId = sessionIdMatch[1];
        
        // Exchange session_id for contributor session
        // Note: Don't use withCredentials to avoid CORS issues with cookies
        const response = await axios.post(
          `${API}/contributors/auth/google/session`,
          { session_id: sessionId }
        );
        
        const { contributor, is_new, session_token } = response.data;
        
        // Get the display name - prioritize name fields over email
        const displayName = contributor.name || contributor.display_name || 
          [contributor.given_name, contributor.family_name].filter(Boolean).join(" ") || 
          (contributor.email ? contributor.email.split('@')[0] : 'User');
        
        // SECURITY NOTE: We store session tokens in localStorage for contributor auth.
        // This is acceptable for contributor accounts (gamification/reviews) as:
        // 1. No sensitive payment or personal data is accessible
        // 2. Session tokens are short-lived and validated on each API call
        // 3. The backend validates all requests with the token
        // For higher-security scenarios, consider httpOnly cookies instead.
        localStorage.setItem("contributor_id", contributor.id);
        localStorage.setItem("contributor_email", contributor.email);
        localStorage.setItem("contributor_name", displayName);
        localStorage.setItem("contributor_display_name", displayName);
        localStorage.setItem("contributor_auth_provider", "google");
        localStorage.setItem("contributor_login_time", new Date().toISOString());
        if (session_token) {
          localStorage.setItem("contributor_session_token", session_token);
        }
        
        // Also store in sessionStorage for backward compatibility
        sessionStorage.setItem("contributor_id", contributor.id);
        sessionStorage.setItem("contributor_email", contributor.email);
        sessionStorage.setItem("contributor_name", displayName);
        if (session_token) {
          sessionStorage.setItem("contributor_session_token", session_token);
        }
        
        // Check if there's a pending review to link
        const pendingReviewId = sessionStorage.getItem("pending_review_id");
        if (pendingReviewId) {
          try {
            const linkResponse = await axios.post(
              `${API}/contributors/link-review?review_id=${pendingReviewId}&contributor_id=${contributor.id}`
            );
            toast.success(linkResponse.data.message || "Review linked! +10 points");
          } catch (linkErr) {
            // Expected: Review might already be linked or not found
            console.debug('Review linking skipped:', linkErr.message);
          }
          sessionStorage.removeItem("pending_review_id");
        }
        
        // Check if there's a pending voucher to redeem
        const pendingVoucherCode = sessionStorage.getItem("pending_voucher_code");
        if (pendingVoucherCode) {
          try {
            const voucherResponse = await axios.post(`${API}/vouchers/redeem`, {
              code: pendingVoucherCode,
              contributor_id: contributor.id,
              contributor_email: contributor.email
            });
            toast.success(voucherResponse.data.message || `Voucher redeemed! +${voucherResponse.data.points_awarded || voucherResponse.data.value} points`);
          } catch (voucherErr) {
            // Voucher might already be redeemed
            const detail = voucherErr.response?.data?.detail;
            if (detail && typeof detail === 'string') {
              toast.error(detail);
            } else {
              // Voucher might already be redeemed - silently continue
            }
          }
          sessionStorage.removeItem("pending_voucher_code");
        }
        
        // Show welcome message
        if (is_new) {
          toast.success(`Welcome to Strider, ${contributor.username || contributor.name}!`);
        } else {
          toast.success(`Welcome back, ${contributor.username || contributor.name}!`);
        }
        
        // Store full contributor data for reuse
        sessionStorage.setItem("contributor", JSON.stringify(contributor));
        
        // Redirect to return URL or contributor page
        const returnUrl = sessionStorage.getItem("auth_return_url");
        const addLocationPending = sessionStorage.getItem("add_location_auth_pending");
        sessionStorage.removeItem("auth_return_url");
        sessionStorage.removeItem("add_location_auth_pending");
        
        // Clean up URL hash
        window.history.replaceState(null, "", window.location.pathname);
        
        // Navigate back to where user came from
        if (returnUrl && returnUrl !== '/contributor/auth/callback') {
          navigate(returnUrl, { 
            replace: true,
            state: { contributor }
          });
        } else if (addLocationPending) {
          navigate("/add-location", { 
            replace: true,
            state: { contributor }
          });
        } else {
          // Navigate to contributor page with user data
          navigate("/contributor", { 
            replace: true,
            state: { contributor }
          });
        }
        
      } catch (err) {
        console.error("Auth error:", err);
        setError(err.response?.data?.detail || "Authentication failed");
        setStatus("error");
      }
    };

    processAuth();
  }, [navigate]);

  if (status === "error") {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Authentication Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate("/contributor")}
            className="btn-primary px-6 py-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin w-12 h-12 border-3 border-brand border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-gray-600">Signing you in...</p>
      </div>
    </div>
  );
}
